import type { StockPoint } from './burnRate';

/**
 * Stock across the months, one row per material.
 *
 * ── Why this is a lens and not a query ─────────────────────────────────────
 *
 * The database holds snapshots: a month, and the rows counted in it. A trend
 * needs the transpose — one row per SKU, one column per month — and building
 * that in the component would mean every row scanning the whole history,
 * which is quadratic on 56 materials × 12 months and gets slower every time
 * the factory files a report.
 *
 * ── The one decision that matters ──────────────────────────────────────────
 *
 * A material absent from a month's report is NULL, never zero. "We did not
 * count it" and "there is none left" are opposite claims, and a table that
 * prints them identically will have somebody reordering against a gap in the
 * paperwork. Every function here keeps that distinction, and the UI prints an
 * em dash for null and a real 0 for zero.
 */

export type Direction = 'down' | 'up' | 'flat' | 'unknown';

export interface MovementRow {
  sku: string;
  description: string;
  /** Quantity per month, aligned to the months array. Null = not counted. */
  quantities: (number | null)[];
  /** Latest counted minus earliest counted, or null with fewer than two. */
  change: number | null;
  direction: Direction;
}

/**
 * The months to show, oldest first.
 *
 * Drawn from the reports themselves rather than from a calendar: a factory
 * that skips March should show a gap between February and April, not an empty
 * March column implying they counted nothing. The gap is the information.
 */
export function recentMonths(history: Map<string, StockPoint[]>, limit: number): string[] {
  const dates = new Set<string>();
  for (const points of history.values()) {
    for (const point of points) {
      dates.add(point.reportDate);
    }
  }
  return [...dates].sort((a, b) => a.localeCompare(b)).slice(-limit);
}

/**
 * One row per material, quantities aligned to `months`.
 *
 * Sorted by the size of the fall, biggest first. A trend table read top-down
 * should answer "what is going fastest" without anybody sorting it, because
 * that is the question somebody opened it with. Materials with no reading at
 * all sink to the bottom rather than sitting at the top as zeros.
 */
export function movementRows(
  history: Map<string, StockPoint[]>,
  descriptions: Map<string, string>,
  months: string[],
): MovementRow[] {
  const rows: MovementRow[] = [];

  for (const [sku, points] of history) {
    const byDate = new Map(points.map((point) => [point.reportDate, point.stockQty]));
    const quantities = months.map((month) => byDate.get(month) ?? null);
    const counted = quantities.filter((value): value is number => value !== null);

    const first = counted[0];
    const last = counted.at(-1);
    const change =
      counted.length < 2 || first === undefined || last === undefined ? null : last - first;

    rows.push({
      sku,
      description: descriptions.get(sku) ?? '',
      quantities,
      change,
      direction: directionOf(change),
    });
  }

  // Steepest fall first. Nulls last — an unknown is not a small change.
  return rows.sort((a, b) => {
    if (a.change === null && b.change === null) return a.sku.localeCompare(b.sku);
    if (a.change === null) return 1;
    if (b.change === null) return -1;
    return a.change - b.change;
  });
}

function directionOf(change: number | null): Direction {
  if (change === null) {
    return 'unknown';
  }
  if (change < 0) {
    return 'down';
  }
  return change > 0 ? 'up' : 'flat';
}

/**
 * The arrow, and what it means in words.
 *
 * "Up" is not good news here and "down" is not bad — a rise means a delivery
 * landed, a fall means the material is being used. The label says the event
 * rather than a verdict, because the panel has no idea which was expected.
 */
export function directionLabel(direction: Direction): string {
  switch (direction) {
    case 'down':
      return 'Used';
    case 'up':
      return 'Restocked';
    case 'flat':
      return 'No change';
    case 'unknown':
      return 'Needs a second report';
  }
}
